import { useToast } from "@/components/podkit/toasts/use-toast";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useStartEnvironment, useStopEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { DeleteEnvironmentModal, ForceDeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { canOpen, environmentHasReachedStablePhase, showStart, showStop } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import type { PlainEditor } from "@/queries/editor-queries";
import {
    ContextMenuItem,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuPortal,
    ContextMenuContent,
    ContextMenuSeparator,
} from "@/components/flexkit/ContextMenu";

export const EnvironmentActionsContextMenu: FC<{
    environment: PlainEnvironment;
    handleViewDetails?: () => void;
    defaultEditor: PlainEditor | undefined;
    startRename: () => void;
    children: React.ReactNode;
}> = ({ environment, handleViewDetails, children, defaultEditor, startRename }) => {
    const { toast } = useToast();
    const navigate = useNavigate();

    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);

    const handleViewLogs = useCallback(() => {
        navigate(`/details/${environment.id}/logs`);
    }, [environment.id, navigate]);

    const handleStart = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync(environment.id);
        } catch (error) {
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
    }, [startEnvironment, environment.id, toast]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: `Environment ID copied to clipboard`,
            description: environment.id,
        });
    }, [toast, environment.id]);

    const openEditor = useOpenEditor();
    const handleStop = useCallback(async () => {
        try {
            await stopEnvironment.mutateAsync(environment.id);
        } catch (error) {
            toast({
                title: "Failed to stop environment",
                description: formatError(error),
            });
        }
    }, [stopEnvironment, toast, environment.id]);

    const handleDelete = useCallback(() => setShowDeleteModal(true), [setShowDeleteModal]);
    const handleForceDelete = useCallback(() => setShowForceDeleteModal(true), [setShowForceDeleteModal]);

    return (
        <>
            <ContextMenuRoot modal={true}>
                <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
                <ContextMenuPortal>
                    <ContextMenuContent className="min-w-[220px] rounded-md bg-surface-01 p-1 shadow-lg">
                        <ContextMenuItem
                            onClick={() => defaultEditor && openEditor(defaultEditor, environment)}
                            disabled={!canOpen(environment)}
                        >
                            Open
                        </ContextMenuItem>
                        <ContextMenuItem onClick={handleViewLogs} data-testid="environment-actions-dropdown-view-logs">
                            View Logs
                        </ContextMenuItem>
                        <ContextMenuSeparator />
                        {handleViewDetails && (
                            <ContextMenuItem onClick={handleViewDetails}>View Details</ContextMenuItem>
                        )}
                        <ContextMenuItem onClick={handleCopyId}>Copy ID</ContextMenuItem>
                        <ContextMenuSeparator />
                        <ContextMenuItem onClick={startRename}>Rename</ContextMenuItem>
                        <ContextMenuItem
                            onClick={handleStart}
                            disabled={!showStart(environment) || !environmentHasReachedStablePhase(environment)}
                            data-testid="environment-actions-dropdown-start"
                        >
                            Start
                        </ContextMenuItem>
                        <ContextMenuItem
                            onClick={handleStop}
                            disabled={!showStop(environment)}
                            data-testid="environment-actions-dropdown-stop"
                        >
                            Stop
                        </ContextMenuItem>
                        {/* Show Delete, when the Environment has not yet been requested to delete */}
                        {environment.spec?.desiredPhase !== EnvironmentPhase.DELETED && (
                            <ContextMenuItem variant="destructive" onClick={handleDelete}>
                                Delete
                            </ContextMenuItem>
                        )}
                        {/* Show Force Delete, when the Environment has been requested to delete */}
                        {environment.spec?.desiredPhase === EnvironmentPhase.DELETED && (
                            <ContextMenuItem onClick={handleForceDelete} variant="destructive">
                                Force Delete
                            </ContextMenuItem>
                        )}
                    </ContextMenuContent>
                </ContextMenuPortal>
            </ContextMenuRoot>
            {showDeleteModal && (
                <DeleteEnvironmentModal environment={environment} onClose={() => setShowDeleteModal(false)} />
            )}
            {showForceDeleteModal && (
                <ForceDeleteEnvironmentModal environment={environment} onClose={() => setShowForceDeleteModal(false)} />
            )}
        </>
    );
};
